import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  Save, Search, Globe, RefreshCw, Settings, FileText, 
  Eye, EyeOff, ChevronDown, ChevronUp, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminSEOSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('global');
  const [globalSettings, setGlobalSettings] = useState({
    site_name: '',
    site_tagline: '',
    default_meta_description: '',
    default_keywords: '',
    default_og_image: '',
    twitter_handle: '',
    facebook_url: '',
    linkedin_url: '',
    organization_name: '',
    organization_phone: '',
    organization_email: '',
    organization_address: '',
    google_site_verification: '',
    bing_site_verification: ''
  });
  const [pageSettings, setPageSettings] = useState([]);
  const [expandedPages, setExpandedPages] = useState({});
  const [selectedPage, setSelectedPage] = useState(null);

  const pages = [
    { slug: 'home', name: 'Homepage', path: '/' },
    { slug: 'solutions', name: 'Solutions', path: '/solutions' },
    { slug: 'features-pricing', name: 'Features & Pricing', path: '/features-pricing' },
    { slug: 'roi-calculator', name: 'ROI Calculator', path: '/roi-calculator' },
    { slug: 'ai-demo', name: 'AI Demo', path: '/ai-demo' },
    { slug: 'ai-features', name: 'AI Features', path: '/ai-features' },
    { slug: 'case-studies', name: 'Case Studies', path: '/case-studies' },
    { slug: 'about', name: 'About Us', path: '/about' },
    { slug: 'contact', name: 'Contact', path: '/contact' },
    { slug: 'book', name: 'Book a Call', path: '/book' },
    { slug: 'blog', name: 'Blog', path: '/blog' },
    { slug: 'privacy-policy', name: 'Privacy Policy', path: '/privacy-policy' },
    { slug: 'terms-of-service', name: 'Terms of Service', path: '/terms-of-service' },
    { slug: 'cookie-policy', name: 'Cookie Policy', path: '/cookie-policy' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Load global settings
      const globalRes = await fetch(`${API_URL}/api/cms/seo/global`);
      if (globalRes.ok) {
        const globalData = await globalRes.json();
        setGlobalSettings(globalData);
      }

      // Load all page settings
      const pagesRes = await fetch(`${API_URL}/api/cms/seo/pages`);
      if (pagesRes.ok) {
        const pagesData = await pagesRes.json();
        setPageSettings(pagesData);
      }
    } catch (error) {
      console.error('Failed to load SEO settings:', error);
      toast.error('Failed to load SEO settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGlobal = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/cms/seo/global`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(globalSettings)
      });
      
      if (!res.ok) throw new Error('Failed to save');
      toast.success('Global SEO settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePage = async (pageSlug) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const pageData = pageSettings.find(p => p.page_slug === pageSlug) || {
        page_slug: pageSlug,
        title: '',
        meta_description: '',
        meta_keywords: '',
        og_title: '',
        og_description: '',
        og_image: '',
        canonical_url: '',
        no_index: false
      };
      
      const res = await fetch(`${API_URL}/api/cms/seo/pages/${pageSlug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(pageData)
      });
      
      if (!res.ok) throw new Error('Failed to save');
      toast.success(`SEO settings for ${pageSlug} saved`);
    } catch (error) {
      toast.error('Failed to save page settings');
    } finally {
      setSaving(false);
    }
  };

  const getPageSetting = (pageSlug) => {
    return pageSettings.find(p => p.page_slug === pageSlug) || {
      page_slug: pageSlug,
      title: '',
      meta_description: '',
      meta_keywords: '',
      og_title: '',
      og_description: '',
      og_image: '',
      canonical_url: '',
      no_index: false
    };
  };

  const updatePageSetting = (pageSlug, field, value) => {
    setPageSettings(prev => {
      const existing = prev.find(p => p.page_slug === pageSlug);
      if (existing) {
        return prev.map(p => p.page_slug === pageSlug ? { ...p, [field]: value } : p);
      }
      return [...prev, { page_slug: pageSlug, [field]: value }];
    });
  };

  const togglePageExpand = (slug) => {
    setExpandedPages(prev => ({ ...prev, [slug]: !prev[slug] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-tech-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-navy">SEO Settings</h1>
          <p className="text-gray-500">Manage meta tags, structured data, and search optimization</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('global')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'global'
              ? 'border-tech-blue text-tech-blue'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Globe className="h-4 w-4" />
          Global Settings
        </button>
        <button
          onClick={() => setActiveTab('pages')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'pages'
              ? 'border-tech-blue text-tech-blue'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText className="h-4 w-4" />
          Page SEO
        </button>
      </div>

      {/* Global Settings Tab */}
      {activeTab === 'global' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-tech-blue" />
                  Site Information
                </span>
                <Button onClick={handleSaveGlobal} disabled={saving} className="bg-tech-blue">
                  {saving ? <RefreshCw className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
                  Save Global Settings
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Site Name</label>
                  <Input
                    value={globalSettings.site_name}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, site_name: e.target.value })}
                    placeholder="GetCare 360"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Site Tagline</label>
                  <Input
                    value={globalSettings.site_tagline}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, site_tagline: e.target.value })}
                    placeholder="NDIS Provider Software Australia"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Default Meta Description</label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-tech-blue/20"
                  rows={3}
                  value={globalSettings.default_meta_description}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, default_meta_description: e.target.value })}
                  placeholder="AI-powered NDIS software platform..."
                />
                <p className="text-xs text-gray-500 mt-1">{globalSettings.default_meta_description?.length || 0}/160 characters</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Default Keywords</label>
                <Input
                  value={globalSettings.default_keywords}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, default_keywords: e.target.value })}
                  placeholder="NDIS software, disability care, compliance..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Default OG Image URL</label>
                <Input
                  value={globalSettings.default_og_image}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, default_og_image: e.target.value })}
                  placeholder="/og-image.jpg"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Organization Details (for Structured Data)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Organization Name</label>
                  <Input
                    value={globalSettings.organization_name}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, organization_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <Input
                    value={globalSettings.organization_phone}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, organization_phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    value={globalSettings.organization_email}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, organization_email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <Input
                    value={globalSettings.organization_address}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, organization_address: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Social & Verification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Twitter Handle</label>
                  <Input
                    value={globalSettings.twitter_handle}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, twitter_handle: e.target.value })}
                    placeholder="@getcare360"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Facebook URL</label>
                  <Input
                    value={globalSettings.facebook_url}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, facebook_url: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">LinkedIn URL</label>
                  <Input
                    value={globalSettings.linkedin_url}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, linkedin_url: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="block text-sm font-medium mb-1">Google Site Verification</label>
                  <Input
                    value={globalSettings.google_site_verification}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, google_site_verification: e.target.value })}
                    placeholder="verification code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bing Site Verification</label>
                  <Input
                    value={globalSettings.bing_site_verification}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, bing_site_verification: e.target.value })}
                    placeholder="verification code"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Page SEO Tab */}
      {activeTab === 'pages' && (
        <div className="space-y-4">
          {pages.map((page) => {
            const settings = getPageSetting(page.slug);
            const isExpanded = expandedPages[page.slug];
            
            return (
              <Card key={page.slug}>
                <div 
                  className="p-4 cursor-pointer flex items-center justify-between hover:bg-gray-50"
                  onClick={() => togglePageExpand(page.slug)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <div>
                      <h3 className="font-medium">{page.name}</h3>
                      <p className="text-sm text-gray-500">{page.path}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {settings.no_index && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">No Index</span>
                    )}
                    <a 
                      href={page.path} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-400 hover:text-tech-blue"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
                
                {isExpanded && (
                  <CardContent className="border-t space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Page Title</label>
                      <Input
                        value={settings.title}
                        onChange={(e) => updatePageSetting(page.slug, 'title', e.target.value)}
                        placeholder={`${page.name} | GetCare 360`}
                      />
                      <p className="text-xs text-gray-500 mt-1">{settings.title?.length || 0}/60 characters recommended</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Meta Description</label>
                      <textarea
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-tech-blue/20"
                        rows={2}
                        value={settings.meta_description}
                        onChange={(e) => updatePageSetting(page.slug, 'meta_description', e.target.value)}
                        placeholder="Page description for search results..."
                      />
                      <p className="text-xs text-gray-500 mt-1">{settings.meta_description?.length || 0}/160 characters recommended</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Keywords</label>
                      <Input
                        value={settings.meta_keywords}
                        onChange={(e) => updatePageSetting(page.slug, 'meta_keywords', e.target.value)}
                        placeholder="keyword1, keyword2, keyword3"
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">OG Title (Social)</label>
                        <Input
                          value={settings.og_title}
                          onChange={(e) => updatePageSetting(page.slug, 'og_title', e.target.value)}
                          placeholder="Leave blank to use page title"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">OG Image URL</label>
                        <Input
                          value={settings.og_image}
                          onChange={(e) => updatePageSetting(page.slug, 'og_image', e.target.value)}
                          placeholder="/og-image.jpg"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">OG Description (Social)</label>
                      <Input
                        value={settings.og_description}
                        onChange={(e) => updatePageSetting(page.slug, 'og_description', e.target.value)}
                        placeholder="Leave blank to use meta description"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.no_index}
                          onChange={(e) => updatePageSetting(page.slug, 'no_index', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm">No Index (hide from search engines)</span>
                      </label>
                      <Button onClick={() => handleSavePage(page.slug)} disabled={saving} size="sm">
                        <Save className="h-4 w-4 mr-2" /> Save Page SEO
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSEOSettingsPage;
